import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { TrustBadges } from "@/components/TrustBadges";

const COLLECTIONS = [
  { label: "Men", to: "/shop?audience=men", img: "https://images.unsplash.com/photo-1701365676249-9d7ab5022dec?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "Boys", to: "/shop?audience=boys", img: "https://images.unsplash.com/photo-1631041047692-884b00d328d5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "New Arrivals", to: "/shop?new_arrival=true", img: "https://images.unsplash.com/photo-1744551358303-46edae8b374b?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "Festive Edit", to: "/shop?occasion=Festive", img: "https://images.unsplash.com/photo-1710242350089-65eef7e49364?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const IG = [
  "https://images.unsplash.com/photo-1768807478287-9e7953cfdca3?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1727835523550-18478cacefa2?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1628250521470-28c1fc54616c?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1652722464455-ec026ef74703?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1561987619-1c9a98f83752?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1774438028955-657a8aaea934?crop=entropy&cs=srgb&fm=jpg&q=85",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [hero, setHero] = useState(null);

  useEffect(() => {
    api.get("/products?featured=true").then(({ data }) => setFeatured(data.slice(0, 8)));
    api.get("/banners").then(({ data }) => setHero(data.find((b) => b.key === "hero")));
    document.title = "KKashigari India — Contemporary Indian Ethnic Wear";
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[86vh] min-h-[560px] overflow-hidden">
        <img src={hero?.image} alt="KKashigari" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2b2415]/55 via-[#2b2415]/20 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 h-full flex flex-col justify-center">
          <div className="max-w-xl fade-up">
            <div className="uplabel text-[#FBF7DE] mb-4">KKashigari · India</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FBF7DE] leading-[0.95] whitespace-pre-line">
              {hero?.title || "Woven in Heritage,\nTailored for Today"}
            </h1>
            <p className="text-[#FBF7DE]/90 mt-6 text-base sm:text-lg max-w-md leading-relaxed">{hero?.subtitle}</p>
            <Link to="/shop" data-testid="hero-cta" className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-4 uplabel">
              {hero?.cta || "Explore The Collection"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Collections */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="uplabel mb-3">Curated For You</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)]">Shop by Collection</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {COLLECTIONS.map((c, i) => (
            <Link key={c.label} to={c.to} data-testid={`collection-${c.label.toLowerCase().replace(/ /g, "-")}`}
              className="group relative overflow-hidden aspect-[3/4] fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2b2415]/70 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <div className="font-serif text-2xl text-[#FBF7DE]">{c.label}</div>
                <div className="uplabel text-[#FBF7DE]/80 flex items-center gap-1 mt-1">Shop Now <ArrowRight className="w-3 h-3" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-20 lg:pb-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="uplabel mb-3">Handpicked</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)]">Featured Pieces</h2>
          </div>
          <Link to="/shop" className="uplabel link-underline hidden sm:block text-[var(--bronze)]">View All</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Brand story teaser */}
      <section className="grid lg:grid-cols-2">
        <div className="relative aspect-[4/3] lg:aspect-auto min-h-[420px]">
          <img src="https://images.unsplash.com/photo-1652722464455-ec026ef74703?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Craftsmanship" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="bg-[var(--bronze)] flex items-center px-8 lg:px-20 py-20">
          <div className="max-w-md">
            <div className="uplabel text-[#FBF7DE]/70 mb-4">The KKashigari Story</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#FBF7DE] leading-tight">Heritage, reimagined for the modern man.</h2>
            <p className="text-[#FBF7DE]/80 mt-6 leading-relaxed">
              Every KKashigari piece begins with the hands of Indian artisans — chikankari threadwork, handloom weaves,
              and quiet detailing. We reinterpret these traditions into clean, contemporary silhouettes made to be worn
              and remembered.
            </p>
            <Link to="/about" data-testid="story-cta" className="inline-flex items-center gap-2 mt-8 uplabel text-[#FBF7DE] link-underline">
              Discover Our Craft <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Instagram strip */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="text-center mb-10">
          <div className="uplabel mb-3">@kkashigari.india</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)]">Follow The Journey</h2>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
          {IG.map((src, i) => (
            <a key={i} href="#" className="group relative overflow-hidden aspect-square">
              <img src={src} alt="Instagram" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
